/** Customers or suppliers (M04-01, M05-01): one screen, two roles. */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount } from '../../lib/money';
import { useAccounts, useAtcs, useTaxCodes, type Contact } from '../../lib/queries';
import { AccountSelect } from '../../components/AccountSelect';
import { Alert, Button, Card, EmptyState, Field, FilterBar, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';

type Role = 'customer' | 'supplier';

export function ContactsPage({ role }: { role: Role }): JSX.Element {
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const list = useQuery({
    queryKey: ['contacts', role, q, showInactive],
    queryFn: () => api<{ data: Contact[]; total: number }>(`/contacts?role=${role}&pageSize=500${q ? `&q=${encodeURIComponent(q)}` : ''}${showInactive ? '' : '&active=true'}`),
    placeholderData: keepPreviousData,
  });
  const canAdd = role === 'customer' ? can('customers.create', 'customers.manage') : can('suppliers.manage');
  const canEdit = role === 'customer' ? can('customers.manage') : can('suppliers.manage');
  const title = role === 'customer' ? 'Customers' : 'Suppliers';

  return (
    <>
      <PageHeader
        title={title}
        description={role === 'customer' ? 'Who you sell to. The TIN and VAT status print on every invoice.' : 'Who you buy from. The default ATC drives the EWT on their bills.'}
        actions={canAdd && <Button onClick={() => setEditing('new')}><Plus aria-hidden="true" className="h-4 w-4" /> New {role}</Button>}
      />
      <FilterBar>
        <input className={`${filterClass} w-64`} placeholder="Name, code or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input type="checkbox" className="h-4 w-4" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
        </label>
      </FilterBar>
      <Card>
        {list.isLoading && <Skeleton className="h-48 w-full" />}
        {list.data?.data.length === 0 && <EmptyState title={`No ${title.toLowerCase()} yet`} />}
        {list.data && list.data.data.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>TIN</Th>
                <Th>VAT</Th>
                <Th>{role === 'customer' ? 'CWT (ATC)' : 'EWT (ATC)'}</Th>
                <Th>Terms</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.data.map((c) => (
                <tr key={c.id} className={c.active ? '' : 'opacity-50'}>
                  <Td className="whitespace-nowrap tabular-nums">{c.code}</Td>
                  <Td>
                    <span className="font-medium">{c.registeredName}</span>
                    {c.isGovernment && <span className="ml-2"><StatusChip tone="info">Government</StatusChip></span>}
                    {c.isCustomer && c.isSupplier && <span className="ml-2"><StatusChip tone="neutral">Both</StatusChip></span>}
                    <span className="block text-xs text-slate-500">{c.address || c.email || ''}</span>
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">{c.tinFormatted ?? <span className="text-amber-600">None</span>}</Td>
                  <Td>{c.vatStatus === 'VAT' ? 'VAT' : c.vatStatus === 'NON_VAT' ? 'Non-VAT' : 'Exempt'}</Td>
                  <Td>{(role === 'customer' ? c.expectedCwtAtc : c.defaultEwtAtc) ?? '—'}</Td>
                  <Td className="whitespace-nowrap">{c.paymentTermsDays} days</Td>
                  <Td align="right">
                    {canEdit && (
                      <button className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label={`Edit ${c.registeredName}`} onClick={() => setEditing(c)}>
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {editing && <ContactDialog role={role} contact={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

export function ContactDialog({ role, contact, onClose, onSaved }: { role: Role; contact: Contact | null; onClose: () => void; onSaved?: (c: Contact) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const atcs = useAtcs();
  const taxCodes = useTaxCodes();
  const accounts = useAccounts();
  const [form, setForm] = useState({
    registeredName: contact?.registeredName ?? '',
    tradeName: contact?.tradeName ?? '',
    isCustomer: contact?.isCustomer ?? role === 'customer',
    isSupplier: contact?.isSupplier ?? role === 'supplier',
    payeeType: contact?.payeeType ?? 'CORPORATE',
    tin: contact?.tin ?? '',
    tinBranchCode: contact?.tinBranchCode ?? '00000',
    vatStatus: contact?.vatStatus ?? 'VAT',
    isGovernment: contact?.isGovernment ?? false,
    street: contact?.street ?? '',
    barangay: contact?.barangay ?? '',
    city: contact?.city ?? '',
    province: contact?.province ?? '',
    zip: contact?.zip ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    contactPerson: contact?.contactPerson ?? '',
    defaultTaxCode: contact?.defaultTaxCode ?? '',
    expectedCwtAtc: contact?.expectedCwtAtc ?? '',
    defaultEwtAtc: contact?.defaultEwtAtc ?? '',
    defaultExpenseAccountId: contact?.defaultExpenseAccountId ?? '',
    paymentTermsDays: String(contact?.paymentTermsDays ?? 30),
    creditLimit: contact?.creditLimit ? amount(contact.creditLimit).replace(/,/g, '') : '',
    bankName: contact?.bankName ?? '',
    bankAccount: '',
    active: contact?.active ?? true,
  });
  const [error, setError] = useState<ApiError | null>(null);
  const set = (patch: Partial<typeof form>): void => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        ...form,
        paymentTermsDays: Number(form.paymentTermsDays || 0),
        tin: form.tin || null,
        defaultTaxCode: form.defaultTaxCode || null,
        expectedCwtAtc: form.expectedCwtAtc || null,
        defaultEwtAtc: form.defaultEwtAtc || null,
        defaultExpenseAccountId: form.defaultExpenseAccountId || null,
        creditLimit: form.creditLimit || null,
      };
      if (!form.bankAccount) delete body.bankAccount;
      if (contact) {
        body.rowVersion = contact.rowVersion;
        return api<Contact>(`/contacts/${contact.id}`, { method: 'PATCH', body });
      }
      delete body.active;
      return api<Contact>('/contacts', { method: 'POST', body });
    },
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      onSaved?.(c);
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  const ewtAtcs = (atcs.data ?? []).filter((a) => a.kind === 'EWT' && a.active);
  const text = (key: keyof typeof form, label: string, extra: { required?: boolean; hint?: string; type?: string } = {}) => (
    <Field label={label} required={extra.required} hint={extra.hint} error={error?.fields[key as string]}>
      <input className={inputClass} type={extra.type ?? 'text'} value={form[key] as string} onChange={(e) => set({ [key]: e.target.value } as Partial<typeof form>)} />
    </Field>
  );

  return (
    <Modal title={contact ? `Edit ${contact.registeredName}` : `New ${role}`} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error.message}</Alert>}
        {text('registeredName', 'Registered name', { required: true })}
        {text('tradeName', 'Trade name / business style')}
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4" checked={form.isCustomer} onChange={(e) => set({ isCustomer: e.target.checked })} /> Customer</label>
          <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4" checked={form.isSupplier} onChange={(e) => set({ isSupplier: e.target.checked })} /> Supplier</label>
          <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4" checked={form.isGovernment} onChange={(e) => set({ isGovernment: e.target.checked })} /> Government agency</label>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">{text('tin', 'TIN', { hint: 'Required for VAT-registered customers.' })}</div>
          {text('tinBranchCode', 'Branch code')}
          <Field label="VAT status">
            <select className={inputClass} value={form.vatStatus} onChange={(e) => set({ vatStatus: e.target.value as typeof form.vatStatus })}>
              <option value="VAT">VAT-registered</option>
              <option value="NON_VAT">Non-VAT</option>
              <option value="VAT_EXEMPT">VAT-exempt</option>
            </select>
          </Field>
          <Field label="Taxpayer">
            <select className={inputClass} value={form.payeeType} onChange={(e) => set({ payeeType: e.target.value as typeof form.payeeType })}>
              <option value="CORPORATE">Corporation / juridical</option>
              <option value="INDIVIDUAL">Individual</option>
            </select>
          </Field>
          {text('paymentTermsDays', 'Terms (days)', { type: 'number' })}
        </div>
        {text('street', 'Street')}
        <div className="grid gap-4 sm:grid-cols-2">
          {text('barangay', 'Barangay')}
          {text('city', 'City / municipality')}
          {text('province', 'Province')}
          {text('zip', 'ZIP')}
          {text('email', 'Email', { type: 'email' })}
          {text('phone', 'Phone')}
          {text('contactPerson', 'Contact person')}
        </div>
        {form.isCustomer && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Default tax code">
              <select className={inputClass} value={form.defaultTaxCode} onChange={(e) => set({ defaultTaxCode: e.target.value })}>
                <option value="">Company default</option>
                {(taxCodes.data ?? []).map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="They withhold from us (CWT)" hint="The ATC on the 2307 they give you.">
              <select className={inputClass} value={form.expectedCwtAtc} onChange={(e) => set({ expectedCwtAtc: e.target.value })}>
                <option value="">None</option>
                {ewtAtcs.map((a) => <option key={a.code} value={a.code}>{a.code} — {(Number(a.rate) * 100).toFixed(0)}% {a.description}</option>)}
              </select>
            </Field>
            {text('creditLimit', 'Credit limit (₱)', { hint: 'Blank = no limit.' })}
          </div>
        )}
        {form.isSupplier && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="We withhold from them (EWT)">
              <select className={inputClass} value={form.defaultEwtAtc} onChange={(e) => set({ defaultEwtAtc: e.target.value })}>
                <option value="">None</option>
                {ewtAtcs.filter((a) => a.payeeType === form.payeeType).map((a) => <option key={a.code} value={a.code}>{a.code} — {(Number(a.rate) * 100).toFixed(0)}% {a.description}</option>)}
              </select>
            </Field>
            <Field label="Default expense account">
              <AccountSelect accounts={(accounts.data ?? []).filter((a) => a.type === 'EXPENSE' || a.type === 'ASSET')} value={form.defaultExpenseAccountId} onChange={(id) => set({ defaultExpenseAccountId: id })} placeholder="None" />
            </Field>
            {text('bankName', 'Bank')}
            <Field label="Bank account no." hint={contact?.bankAccountMasked ? `On file: ${contact.bankAccountMasked}. Type to replace.` : 'Stored encrypted; only the last 4 digits are shown.'}>
              <input className={inputClass} value={form.bankAccount} onChange={(e) => set({ bankAccount: e.target.value })} autoComplete="off" />
            </Field>
          </div>
        )}
        {contact && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={form.active} onChange={(e) => set({ active: e.target.checked })} /> Active (inactive contacts stay on old documents but can't be used on new ones)
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
